import React, { useEffect, useMemo, useState } from "react";
import {
  IonModal,
  IonContent,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
} from "@ionic/react";

import {
  sendRealEmailOtp,
  linkSocialAccount,
} from "../../services/accountEmail";
import { toErrorText } from "../../services/errorText";
import "./RecoverAppleAccountSheet.css";

const OTP_LEN = 4;

const isEmailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || "").trim());

/**
 * Shown right after an Apple "Hide My Email" sign-in that created a brand-new
 * account (backend flags email_is_private_relay + is_new_account). Offers to
 * recover a previously-used account/subscription by verifying the real email,
 * or to continue with a fresh account using the relay address.
 *
 * Not swipe-dismissible: the user must pick one of the two paths. "Continua con
 * un nuovo account" is the escape hatch.
 */
const RecoverAppleAccountSheet = ({ isOpen, onContinueNew, onRecovered }) => {
  const [phase, setPhase] = useState("choice"); // "choice" | "email" | "otp"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPhase("choice");
      setEmail("");
      setOtp("");
      setError("");
      setLoading(false);
    }
  }, [isOpen]);

  const emailTrimmed = useMemo(() => (email || "").trim(), [email]);

  const requestOtp = async () => {
    if (!isEmailValid(emailTrimmed) || loading) return;
    setError("");
    setLoading(true);
    try {
      await sendRealEmailOtp(emailTrimmed);
      setOtp("");
      setPhase("otp");
    } catch (e) {
      setError(toErrorText(e, "Errore durante l'invio del codice."));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (otp.trim().length < OTP_LEN || loading) return;
    setError("");
    setLoading(true);
    try {
      const data = await linkSocialAccount(emailTrimmed, otp.trim());
      if (!data?.token) throw new Error("Risposta inattesa dal server.");
      onRecovered?.(data.token);
    } catch (e) {
      setError(toErrorText(e, "Impossibile recuperare l'account."));
      setLoading(false);
    }
    // On success the parent swaps the session + routes; this sheet unmounts.
  };

  return (
    <IonModal
      isOpen={isOpen}
      backdropDismiss={false}
      className="recover-apple-sheet"
    >
      <IonContent className="ion-padding recover-apple-sheet__content">
        <div className="recover-apple-sheet__inner">
          {phase === "choice" && (
            <>
              <h2 className="recover-apple-sheet__title">
                Apple ha nascosto la tua email
              </h2>
              <IonText className="recover-apple-sheet__subtitle">
                Per recuperare un account o un abbonamento Dieta Social esistente,
                conferma l'indirizzo email che hai usato in precedenza.
              </IonText>

              <IonButton
                expand="block"
                shape="round"
                onClick={() => {
                  setError("");
                  setPhase("email");
                }}
              >
                Recupera il mio account
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => onContinueNew?.()}
              >
                Continua con un nuovo account
              </IonButton>
            </>
          )}

          {phase === "email" && (
            <>
              <h2 className="recover-apple-sheet__title">Conferma la tua email</h2>
              <IonText className="recover-apple-sheet__subtitle">
                Inserisci l'email che hai usato in precedenza: ti invieremo un
                codice per verificarla.
              </IonText>

              <div className="recover-apple-sheet__field">
                <label className="recover-apple-sheet__label">Email</label>
                <IonInput
                  value={email}
                  onIonInput={(e) => setEmail(e.detail?.value ?? "")}
                  type="email"
                  inputMode="email"
                  autocapitalize="off"
                  placeholder="latua@email.com"
                  className="recover-apple-sheet__input"
                />
              </div>

              {!!error && (
                <IonText color="danger" className="recover-apple-sheet__error">
                  {error}
                </IonText>
              )}

              <IonButton
                expand="block"
                shape="round"
                disabled={!isEmailValid(emailTrimmed) || loading}
                onClick={requestOtp}
              >
                {loading ? <IonSpinner name="dots" /> : "Invia codice"}
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                disabled={loading}
                onClick={() => {
                  setError("");
                  setPhase("choice");
                }}
              >
                Indietro
              </IonButton>
            </>
          )}

          {phase === "otp" && (
            <>
              <h2 className="recover-apple-sheet__title">Verifica l'email</h2>
              <IonText className="recover-apple-sheet__subtitle">
                Inserisci il codice di verifica che abbiamo inviato a{" "}
                <strong>{emailTrimmed}</strong>.
              </IonText>

              <div className="recover-apple-sheet__field">
                <label className="recover-apple-sheet__label">Codice</label>
                <IonInput
                  value={otp}
                  onIonInput={(e) => setOtp(e.detail?.value ?? "")}
                  inputMode="numeric"
                  type="tel"
                  maxlength={8}
                  placeholder="––––"
                  className="recover-apple-sheet__input recover-apple-sheet__input--otp"
                  autofocus
                />
              </div>

              {!!error && (
                <IonText color="danger" className="recover-apple-sheet__error">
                  {error}
                </IonText>
              )}

              <IonButton
                expand="block"
                shape="round"
                disabled={otp.trim().length < OTP_LEN || loading}
                onClick={confirm}
              >
                {loading ? <IonSpinner name="dots" /> : "Recupera account"}
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                disabled={loading}
                onClick={() => {
                  setError("");
                  setPhase("email");
                }}
              >
                Cambia email
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default RecoverAppleAccountSheet;
